<template>
  <div class="observers-card">
    <div class="observers-card-header">
      <div class="text-grey-darken-4 font-weight-bold">
        {{ i18n.t('titles.observers') }}
      </div>

      <div class="mr-3" data-test-id="edit-observers">
        <v-btn
          class="px-0 p-0"
          color="primary"
          data-test-id="edit-observers-deep-text"
          variant="plain"
          @click="editObservers"
        >
          {{
            hasDefinedObservers ? i18n.t('buttons.edit') : i18n.t('buttons.add')
          }}
        </v-btn>
      </div>
    </div>

    <div class="observers-card-content" :class="{ 'pb-4': hasObservers }">
      <template
        v-for="observer in document.observers"
        :key="observer.observerId"
      >
        <ObserverItem
          v-if="observer.email && observer.email !== ''"
          :observer="observer"
        />
      </template>

      <div
        v-if="undefinedObserversCount > 0"
        class="drawer-subhead text-grey undefined-observer-count"
        data-test-id="undefined-observer-count"
      >
        +
        {{
          i18n.t('texts.undefined_observers', {
            number: undefinedObserversCount
          })
        }}
      </div>
    </div>
    <ObserversDialog :document="document" />
  </div>
</template>

<script setup lang="ts">
import ObserversDialog from '@/components/dialogs/ObserversDialog.vue';
import ObserverItem from '@/components/items/ObserverItem.vue';
import { useEmitter } from '@/composables/useEmitter';
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Document } from '@/types/Document';

const props = defineProps({
  document: {
    type: Object as PropType<Document>,
    required: true
  }
});

const emitter = useEmitter();
const i18n = useI18n();

const hasDefinedObservers = computed(
  () =>
    props.document?.observers?.some(
      (observer) => !!observer.email && observer.email !== ''
    ) || false
);

const hasObservers = computed(
  () => (props.document?.observers?.length || 0) > 0
);

const undefinedObserversCount = computed(
  () =>
    props.document?.observers?.filter((obs) => obs.email === null).length || 0
);

function editObservers() {
  emitter.$emit('open-observers-dialog', props.document?.observers);
}
</script>

<style scoped lang="scss">
.observers-card {
  display: flex;
  flex-direction: column;
  row-gap: 2px;
}

.observers-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 49px;
}

.observers-card-content {
  display: flex;
  flex-direction: column;
  row-gap: 14px;
  margin-left: 8px;
}

:deep(.v-btn--size-default) {
  padding: 0 !important;
  text-align: right !important;
  justify-content: flex-end !important;
}
</style>
